import { useMemo } from "react";
import type { AnalysisVersionResponse, MatchingResponse } from "@/api/types";
import { ArtifactView } from "@/components/artifacts/ArtifactView";
import { entitiesFromMatching } from "@/components/artifacts/entityHighlight";

export interface ArtifactsTabProps {
  version: AnalysisVersionResponse | null;
  /**
   * Optional callback for citation-timestamp clicks. When provided, the
   * meeting-card view switches to the Transcript tab and scrolls to the
   * utterance at the supplied `start_ms`. Threaded through ArtifactView →
   * CitationBlock unchanged.
   */
  onJumpToTranscript?: (startMs: number) => void;
  /**
   * Phase 6 matching snapshot. Bound employees/clients become highlight
   * targets inside the artifact text. `null` (or empty bindings) → text
   * renders verbatim, no `<mark>` nodes emitted.
   */
  matching?: MatchingResponse | null;
}

export function ArtifactsTab({
  version,
  onJumpToTranscript,
  matching = null,
}: ArtifactsTabProps) {
  const highlightEntities = useMemo(
    () => entitiesFromMatching(matching ?? null),
    [matching],
  );
  if (version === null || version.artifacts.length === 0) {
    return <p className="text-sm text-muted-foreground">—</p>;
  }
  return (
    <div className="space-y-6" data-testid="artifacts-tab">
      {version.artifacts.map((a, i) => (
        <ArtifactView
          key={i}
          artifact={a}
          onJumpToTranscript={onJumpToTranscript}
          highlightEntities={highlightEntities}
        />
      ))}
    </div>
  );
}
